import { $Enums } from '@vae/server/dist/client'
import { useCallback, useEffect, useRef } from 'react'
import useUser from '../contexts/hooks/useUser'
import usePreference from '../utils/preferences/usePreference'

interface UseThemeHandlerProps {
	onThemeChange: (isDark: boolean) => void
}

export const useThemeHandler = ({ onThemeChange }: UseThemeHandlerProps) => {
	const { userSettings } = useUser()

	const systemDarkQueryRef = useRef<MediaQueryList>()
	const [storedMode, setStoredMode] = usePreference('preferredTheme', 'system')

	const applyTheme = useCallback(
		(mode: $Enums.ThemeMode) => {
			if (!systemDarkQueryRef.current) {
				systemDarkQueryRef.current = window.matchMedia('(prefers-color-scheme: dark)')
			}

			const isDark = mode === 'dark' || (mode === 'system' && systemDarkQueryRef.current.matches)
			document.body.classList.toggle('dark', isDark)
			onThemeChange(isDark)
		},
		[onThemeChange]
	)

	const setThemeMode = useCallback(
		(mode: $Enums.ThemeMode) => {
			setStoredMode(mode)
			applyTheme(mode)
		},
		[applyTheme]
	)

	useEffect(() => {
		const handleSystemThemeChange = async () => {
			const currentMode = userSettings?.themeMode || storedMode || 'system'
			if (currentMode === 'system') {
				applyTheme('system')
			}
		}

		const initTheme = async () => {
			systemDarkQueryRef.current = window.matchMedia('(prefers-color-scheme: dark)')
			const themeMode = userSettings?.themeMode || storedMode || 'system'

			applyTheme(themeMode)
			systemDarkQueryRef.current.addEventListener('change', handleSystemThemeChange)
		}

		initTheme()

		return () => systemDarkQueryRef.current?.removeEventListener('change', handleSystemThemeChange)
	}, [userSettings, applyTheme, storedMode])

	return { setThemeMode }
}
